/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useTranslation } from 'react-i18next';

// UI lib components
import {
	Box,
	Flex,
	Group,
	Image,
	Paper,
	Skeleton,
} from '@mantine/core';

// UI local components
import {
	ProjectsSection,
	WelcomeMessage,
} from '@features/dashboard/components';

// Styles
import BatimentIcon from '@assets/icons/batiment.svg';
import { useBrandKpis } from '@features/dashboard/api/brand-kpis';
import { CompensationSection } from '@features/dashboard/components/CompensationSection';
import MetricCard from '@features/dashboard/components/MetricCard/MetricCard';
import { SelectV2 } from '@features/dashboard/components/SelectV2/SelectV2';
import useStore from '@store/index';
import {
	convertArrayToLabelValue,
	convertGCO2EToTCO2EWithoutUnitFloor,
	truncateString,
} from '@utils/functions';
import isNil from 'lodash/isNil';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useBrands } from 'shared/api/get-brands';
import classes from './dashboard.module.css';

import { queryKeys } from '@config/constants/query-keys';
import { ROLES } from '@config/constants/roles';
import { useGetAllMeasurabilities } from '@features/dashboard/api/get-all-measurabilities';
import { useLatestOffsetsQuery } from '@features/dashboard/api/get-latest-offsets';
import { useLatestProjectsQuery } from '@features/dashboard/api/get-latest-projects';
import { useProductions } from '@features/dashboard/api/get-productions';
import { useProjectKpis } from '@features/dashboard/api/project-kpis';
import { DateFilter } from '@features/dashboard/components/DateFilter';
import { ProductionsSection } from '@features/dashboard/components/ProductionsSection';
import { useCreateCertificate } from '@features/offsets/api/create-certificate';
import { getCertificate } from '@features/offsets/api/get-certificate';
import { TCertificate } from '@global-types/index';
import {
	Authorization,
	useAuthorization,
} from '@lib/authorization';
import { queryClient } from '@lib/react-query';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import findIndex from 'lodash/findIndex';

const Dashboard = () => {
	/* ********************************** HOOKS ********************************* */
	const { setOffsetOpenDrawer, getUser } = useStore();
	const { checkRole, entity, role } = useAuthorization();

	const user = getUser();

	const [queryParamsDates, setQueryParamsDates] =
		useState<any>({
			...(role == ROLES.ADMIN ||
			role == ROLES.SUPERADMIN ||
			role == ROLES.STAFF
				? {
						startDate: dayjs().startOf('year'),
						endDate: dayjs().endOf('year'),
					}
				: {}),
		});

	const { mutate } = useCreateCertificate();

	const navigate = useNavigate();

	const { data: brands, isPending } = useBrands();

	const [queryParams, setQueryParams] = useState<any>({
		...(role == ROLES.ADMIN ||
		role == ROLES.SUPERADMIN ||
		role == ROLES.STAFF
			? {
					startDate: dayjs().startOf('year'),
					endDate: dayjs().endOf('year'),
				}
			: {}),

		kpis: [
			'SUM_OFFSET_AMOUNT_GCO2',
			'SUM_OFFSET_AMOUNT_EURO',
		],
		...((entity && role == ROLES.BRAND_ADMIN) ||
		role == ROLES.BRAND_STAFF
			? {
					ids: [entity],
				}
			: {}),
	});

	const [selectedProjet, setSelectedProject] = useState<
		string | null
	>(null);

	const { t, i18n } = useTranslation();

	const {
		data: statisticsBrandKpis,
		mutate: statisticsBrandKpisMutation,
		isPending: statisticsBrandKpisIsPending,
	} = useBrandKpis({
		mutationConfig: {},
	});
	const {
		data: statisticsProjectKpis,
		mutate: statisticsProjectKpisMutation,
		isPending: statisticsProjectKpisIsPending,
	} = useProjectKpis({
		mutationConfig: {},
	});

	const {
		data: productions,
		isLoading: isLoadingProductions,
	} = useProductions(1, 6);

	const {
		idsMeasurabilities: idsMeasurabilities,
		data: measurabilitiesItems,
		isFetched: isFetchedMeasurabilitiesIds,
		isPending: isPendingAllMeasurabilities,
	} = useGetAllMeasurabilities({});

	useEffect(() => {
		if (
			checkRole([
				ROLES.PRODUCER_ADMIN,
				ROLES.PRODUCER_STAFF,
				ROLES.ORGANIZATION_ADMIN,
				ROLES.ORGANIZATION_STAFF,
			]) &&
			isFetchedMeasurabilitiesIds
		) {
			const newQueryParams = {
				...queryParams,
				ids: idsMeasurabilities,
			};

			statisticsProjectKpisMutation(newQueryParams);
		}
	}, [isFetchedMeasurabilitiesIds]);

	useEffect(() => {
		if (
			!checkRole([
				ROLES.PRODUCER_ADMIN,
				ROLES.PRODUCER_STAFF,
				ROLES.ORGANIZATION_ADMIN,
				ROLES.ORGANIZATION_STAFF,
			])
		) {
			statisticsBrandKpisMutation(queryParams);
		}
	}, []);

	const {
		data: measurabilities,
		isPending: isPendingGetLatestProjects,
	} = useLatestProjectsQuery({
		queryParams: {
			pageSize: 3,
			...(queryParamsDates
				? {
						query: {
							createdAt: {
								$gte: queryParamsDates.startDate,
								$lt: queryParamsDates.endDate,
							},
						},
					}
				: {}),
		},
		sortParams: '-createdAt',
	});

	const {
		data: offsets,
		isPending: isPendingGetLatestOffsets,
	} = useLatestOffsetsQuery({
		queryParams: {
			pageSize: 6,
			...(queryParamsDates
				? {
						query: {
							createdAt: {
								$gte: queryParamsDates.startDate,
								$lt: queryParamsDates.endDate,
							},
						},
					}
				: {}),
			...(queryParams?.ids?.length > 0
				? { entity: queryParams.ids[0] }
				: {}),
		},
		sortParams: '-createdAt',
	});

	const handleDownloadCertificateApi = async (
		id: string,
		nameOffset: string,
		nameBrand: string,
		contributionDate: string,
		certificateId: string,
		lang: string,
		certificates: TCertificate[]
	) => {
		const idNotification = notifications.show({
			loading: true,
			title: `${t(
				`offsets.notifications.loadingTheCertificate`
			)} ${truncateString(nameOffset, 18)}`,
			message: `${t(
				`offsets.notifications.theDownloadOfCertification`
			)} ${nameOffset} ${t(
				`offsets.notifications.isCurrentlyInProgress`
			)}`,
			autoClose: false,
			withCloseButton: false,
		});

		const parsedContributionDate = dayjs(
			contributionDate,
			'DD/MM/YYYY'
		).format('DD-MM-YYYY');

		let offsetHasCertificate: boolean = false;

		if (certificates && Array.isArray(certificates)) {
			offsetHasCertificate =
				findIndex(certificates, {
					language: lang,
				}) !== -1;
		}

		if (offsetHasCertificate) {
			await getCertificate(
				certificateId,
				nameOffset,
				nameBrand,
				parsedContributionDate,
				idNotification
			);
		} else {
			mutate(
				{
					offset: id,
					language: lang,
				},
				{
					onSuccess: async (response) => {
						await getCertificate(
							response.id,
							nameOffset,
							nameBrand,
							parsedContributionDate,
							idNotification
						);

						queryClient.invalidateQueries({
							queryKey: [
								queryKeys.app.dashboard.latestOffsets.key,
							],
						});
					},
				}
			);
		}
	};

	const handleDownloadCertificate = async (data: any) => {
		const { certificates } = data;
		const foundCertificate = certificates.find(
			(item: any) => item.language == i18n.language
		);

		await handleDownloadCertificateApi(
			data.id,
			data.name,
			data.entity.name,
			data.contributionDate,
			foundCertificate?.id || '',
			i18n.language,
			data.certificates
		);
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Paper className={classes.container}>
			{/* Filters */}
			<Flex
				wrap={'wrap'}
				align="center"
				justify="space-between"
				gap={'15px'}
			>
				<WelcomeMessage
					username={user?.firstName || 'Inuk'}
				/>
				<Group align="center" gap={'15px'}>
					<Authorization
						allowedRoles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
					>
						{isPending ? (
							<Skeleton width={'154px'} height={'38px'} />
						) : (
							<Box
								style={{
									minWidth: '250px',
								}}
							>
								<SelectV2
									clearable
									data={convertArrayToLabelValue(brands)}
									placeholder={t(
										'placeholders.selectEntreprise'
									)}
									leftSection={
										<Image
											className={classes.iconBrand}
											src={BatimentIcon}
										/>
									}
									onChange={(option) => {
										let body = {};
										if (isNil(option)) {
											body = {
												kpis: [
													'SUM_OFFSET_AMOUNT_GCO2',
													'SUM_OFFSET_AMOUNT_EURO',
													'SUM_PLEDGED_ADDI_PRODUCED',
												],
											};
											setQueryParams(body);
										} else {
											body = {
												...queryParams,
												ids: [option],
											};
											setQueryParams(body);
										}

										statisticsBrandKpisMutation(body);
									}}
								/>
							</Box>
						)}
					</Authorization>
					<Authorization
						allowedRoles={[
							ROLES.PRODUCER_ADMIN,
							ROLES.PRODUCER_STAFF,
							ROLES.ORGANIZATION_ADMIN,
							ROLES.ORGANIZATION_STAFF,
						]}
					>
						{isPendingAllMeasurabilities ? (
							<Skeleton width={'154px'} height={'38px'} />
						) : Array.isArray(measurabilitiesItems) &&
						  measurabilitiesItems.length > 1 ? (
							<Box
								style={{
									minWidth: '250px',
								}}
							>
								<SelectV2
									clearable
									data={convertArrayToLabelValue(
										measurabilitiesItems
									)}
									placeholder={t(
										'placeholders.selectProject'
									)}
									onChange={(option) => {
										setSelectedProject(option);
									}}
								/>
							</Box>
						) : null}
					</Authorization>
					<Box
						style={{
							minWidth: '200px',
						}}
					>
						<DateFilter
							size="sm"
							onApply={(data) => {
								const {
									startDate,
									endDate,
									...restQueryParams
								} = queryParams;

								let body = {};
								body = {
									...restQueryParams,
									...(!isNil(data[0].value[0])
										? {
												startDate: dayjs(
													data[0].value[0]
												).format('YYYY-MM-DD'),
											}
										: {}),
									...(!isNil(data[0].value[1])
										? {
												endDate: dayjs(
													data[0].value[1]
												).format('YYYY-MM-DD'),
											}
										: {}),
								};

								setQueryParams(body);
								if (
									checkRole([
										ROLES.PRODUCER_ADMIN,
										ROLES.PRODUCER_STAFF,
										ROLES.ORGANIZATION_ADMIN,
									])
								) {
									statisticsProjectKpisMutation(body);
								} else {
									statisticsBrandKpisMutation(body);
								}

								setQueryParamsDates({
									...(!isNil(data[0].value[0])
										? {
												startDate: dayjs(
													data[0].value[0]
												).format('YYYY-MM-DD'),
											}
										: {}),
									...(!isNil(data[0].value[1])
										? {
												endDate: dayjs(
													data[0].value[1]
												).format('YYYY-MM-DD'),
											}
										: {}),
								});
							}}
							placeholder={t(
								'placeholders.searchProject'
							)}
							init={
								role == ROLES.USER ? 'allData' : 'year'
							}
						/>
					</Box>
				</Group>
			</Flex>
			{/* KPIs */}
			<Flex wrap={'wrap'} align="center" gap={'24px'}>
				<Authorization
					allowedRoles={[
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
					]}
				>
					<Box className={classes.bloc}>
						<MetricCard
							title={t('global.launchContribution')}
							type={'addOffset'}
							isLoading={false}
							onClick={() => {
								setOffsetOpenDrawer(true);
							}}
						/>
					</Box>
				</Authorization>
				<Authorization
					allowedRoles={[
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
						ROLES.USER,
						ROLES.ORGANIZATION_MEMBER,
						ROLES.BRAND_ADMIN,
						ROLES.BRAND_STAFF,
					]}
				>
					<Box className={classes.bloc}>
						<MetricCard
							title={t('global.compensatedEmissions')}
							value={convertGCO2EToTCO2EWithoutUnitFloor(
								statisticsBrandKpis?.kpis
									?.SUM_OFFSET_AMOUNT_GCO2 || 0
							)}
							type={'emissionsCompensated'}
							isLoading={statisticsBrandKpisIsPending}
						/>
					</Box>
					<Box className={classes.bloc}>
						<MetricCard
							title={t('global.totalCompensation')}
							value={Math.floor(
								statisticsBrandKpis?.kpis
									?.SUM_OFFSET_AMOUNT_EURO || 0
							)}
							type={'totalCompensation'}
							isLoading={statisticsBrandKpisIsPending}
						/>
					</Box>
				</Authorization>
				<Authorization
					allowedRoles={[
						ROLES.PRODUCER_ADMIN,
						ROLES.PRODUCER_STAFF,
						ROLES.ORGANIZATION_ADMIN,
						ROLES.ORGANIZATION_STAFF,
					]}
				>
					<Box className={classes.bloc}>
						<MetricCard
							title={t('global.carbonCreditsSold')}
							value={convertGCO2EToTCO2EWithoutUnitFloor(
								measurabilitiesItems &&
									measurabilitiesItems.length > 0
									? isNil(selectedProjet)
										? statisticsProjectKpis?.kpis
												?.SUM_OFFSET_AMOUNT_GCO2 || 0
										: statisticsProjectKpis?.projects.find(
												(item) =>
													item.id === selectedProjet
											)?.kpis?.SUM_OFFSET_AMOUNT_GCO2 || 0
									: 0
							)}
							type={'emissionsCompensated'}
							isLoading={statisticsBrandKpisIsPending}
						/>
					</Box>
					<Authorization
						allowedRoles={[
							ROLES.ORGANIZATION_ADMIN,
							ROLES.ORGANIZATION_STAFF,
						]}
					>
						<Box className={classes.bloc}>
							<MetricCard
								title={t('global.totalSalesAmount')}
								value={Math.floor(
									measurabilitiesItems &&
										measurabilitiesItems.length > 0
										? isNil(selectedProjet)
											? statisticsProjectKpis?.kpis
													?.SUM_OFFSET_AMOUNT_EURO || 0
											: statisticsProjectKpis?.projects.find(
													(item) =>
														item.id === selectedProjet
												)?.kpis?.SUM_OFFSET_AMOUNT_EURO ||
												0
										: 0
								)}
								type={'totalCompensation'}
								isLoading={statisticsBrandKpisIsPending}
							/>
						</Box>
					</Authorization>
				</Authorization>

				<Authorization
					allowedRoles={[
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
						ROLES.ORGANIZATION_ADMIN,
						ROLES.ORGANIZATION_STAFF,
					]}
				>
					<Box className={classes.bloc}>
						<MetricCard
							type={'addAShortcut'}
							isLoading={false}
						/>
					</Box>
				</Authorization>
			</Flex>
			{/* Offsets & Projects & Productions */}
			<Box
				className={classes.containerProjectsCompensations}
			>
				<Box className={classes.bloc}>
					<ProjectsSection
						isLoading={isPendingGetLatestProjects}
						data={measurabilities || []}
						onSeeAll={() => {
							navigate('/measurability-projects');
						}}
					/>
				</Box>
				<Authorization
					allowedRoles={[
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
						ROLES.USER,
						ROLES.ORGANIZATION_ADMIN,
						ROLES.ORGANIZATION_MEMBER,
						ROLES.ORGANIZATION_STAFF,
						ROLES.BRAND_ADMIN,
						ROLES.BRAND_STAFF,
					]}
				>
					<Box className={classes.bloc}>
						<CompensationSection
							isLoading={isPendingGetLatestOffsets}
							data={offsets || []}
							onSeeAll={() => {
								navigate('/offsets');
							}}
							onDownload={handleDownloadCertificate}
						/>
					</Box>
				</Authorization>
				{/* Production list view tailored for users with the 'producers' role */}
				<Authorization
					allowedRoles={[
						ROLES.PRODUCER_ADMIN,
						ROLES.PRODUCER_STAFF,
					]}
				>
					<Box className={classes.bloc}>
						<ProductionsSection
							isLoading={isLoadingProductions}
							data={
								Array.isArray(measurabilitiesItems) &&
								measurabilitiesItems.length > 0
									? productions
									: []
							}
							onSeeAll={() => {
								navigate('/productions');
							}}
						/>
					</Box>
				</Authorization>
			</Box>
		</Paper>
	);
};

export default Dashboard;
